/**
 * The low-poly person: body plan and part geometry.
 *
 * Modelled on the figures in the diorama reference — realistic proportions
 * rather than chibi, about seven heads tall, every part a faceted prism. Each
 * part is its own geometry with its pivot at the joint it hangs from, so the
 * crowd can pose a character by composing a handful of rotations and nothing
 * needs a skeleton or a skinning pass.
 *
 * Parts carry a white vertex-colour attribute; the crowd supplies clothing and
 * skin tones per instance, and the shader multiplies the two. The only
 * non-white vertices are the eyes, which is how a head can be dark-eyed and
 * any skin tone at once without a second draw call.
 */

import type { BufferGeometry } from 'three';
import { BoxGeometry, BufferAttribute, Color, CylinderGeometry } from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { BODY } from '../../core/character/body.js';

/** Joint positions: see `core/character/body.ts`. */
export { BODY };

interface PrismSpec {
  sides: number;
  /** Bottom and top of the prism along Y. */
  y0: number;
  y1: number;
  /** Half-extents in X and Z at the bottom and the top. */
  bottom: readonly [number, number];
  top: readonly [number, number];
  offset?: readonly [number, number];
  /** Vertex colour, for the rare part that is not tinted per instance. */
  shade?: number;
}

/**
 * A faceted, tapered, possibly elliptical prism.
 *
 * Built from a unit cylinder and reshaped per vertex, then de-indexed so every
 * facet gets its own normal. Turned half a facet so a flat face — not an edge —
 * points forward, which is what makes a hexagonal torso read as a chest.
 */
function prism(spec: PrismSpec): BufferGeometry {
  const height = spec.y1 - spec.y0;
  const base = new CylinderGeometry(1, 1, height, spec.sides, 1, false, Math.PI / spec.sides);
  const position = base.getAttribute('position');
  for (let i = 0; i < position.count; i++) {
    const y = position.getY(i);
    const t = (y + height / 2) / height;
    const rx = spec.bottom[0] + (spec.top[0] - spec.bottom[0]) * t;
    const rz = spec.bottom[1] + (spec.top[1] - spec.bottom[1]) * t;
    position.setXYZ(
      i,
      position.getX(i) * rx + (spec.offset?.[0] ?? 0),
      y + spec.y0 + height / 2,
      position.getZ(i) * rz + (spec.offset?.[1] ?? 0),
    );
  }
  return finish(base, spec.shade ?? 1);
}

function block(w: number, h: number, d: number, x: number, y: number, z: number, shade = 1): BufferGeometry {
  return finish(new BoxGeometry(w, h, d).translate(x, y, z), shade);
}

/** De-index, give every facet its own normal, and paint the vertex colour. */
function finish(geometry: BufferGeometry, shade: number): BufferGeometry {
  const flat = geometry.index === null ? geometry : geometry.toNonIndexed();
  if (flat !== geometry) geometry.dispose();
  flat.deleteAttribute('uv');
  flat.computeVertexNormals();
  const count = flat.getAttribute('position').count;
  flat.setAttribute('color', new BufferAttribute(new Float32Array(count * 3).fill(shade), 3));
  return flat;
}

function merge(parts: BufferGeometry[]): BufferGeometry {
  const merged = mergeGeometries(parts);
  parts.forEach(p => p.dispose());
  return merged;
}

/** Eye colour, as a multiplier on the skin tone. */
const EYE = 0.08;

export function createPartGeometries() {
  const head = merge([
    prism({ sides: 6, y0: -0.01, y1: 0.08, bottom: [0.048, 0.045], top: [0.044, 0.042] }),
    prism({ sides: 8, y0: 0.06, y1: 0.225, bottom: [0.093, 0.1], top: [0.099, 0.107], offset: [0, 0.005] }),
    // Crown, tapering in so the hair cap always covers it.
    prism({ sides: 8, y0: 0.224, y1: 0.28, bottom: [0.099, 0.107], top: [0.08, 0.087], offset: [0, 0.005] }),
    // Eyes: two dark blocks on the face. Tiny, but they are what makes a
    // figure read as someone rather than something at chase-camera distance.
    block(0.026, 0.03, 0.012, -0.038, 0.17, -0.099, EYE),
    block(0.026, 0.03, 0.012, 0.038, 0.17, -0.099, EYE),
    // A nose, a single facet proud of the face, catches the sun.
    prism({ sides: 4, y0: 0.125, y1: 0.16, bottom: [0.016, 0.018], top: [0.011, 0.008], offset: [0, -0.1] }),
  ]);

  const hairShort = merge([
    prism({ sides: 8, y0: 0.18, y1: 0.305, bottom: [0.107, 0.115], top: [0.092, 0.099], offset: [0, 0.006] }),
    prism({ sides: 6, y0: 0.07, y1: 0.21, bottom: [0.09, 0.055], top: [0.104, 0.07], offset: [0, 0.058] }),
  ]);

  const hairLong = merge([
    prism({ sides: 8, y0: 0.18, y1: 0.31, bottom: [0.11, 0.118], top: [0.094, 0.101], offset: [0, 0.006] }),
    prism({ sides: 6, y0: -0.1, y1: 0.22, bottom: [0.13, 0.06], top: [0.112, 0.075], offset: [0, 0.06] }),
    // Side locks framing the face.
    block(0.03, 0.2, 0.09, -0.098, 0.12, 0.0),
    block(0.03, 0.2, 0.09, 0.098, 0.12, 0.0),
  ]);

  const torso = merge([
    prism({ sides: 6, y0: 0, y1: BODY.torsoHeight, bottom: [0.152, 0.098], top: [0.2, 0.108] }),
    // Shoulder yoke, so the arms have something to hang from.
    prism({ sides: 6, y0: BODY.torsoHeight - 0.07, y1: BODY.torsoHeight + 0.005, bottom: [0.222, 0.1], top: [0.16, 0.085] }),
  ]);

  const hipsShorts = prism({ sides: 6, y0: -0.08, y1: 0.08, bottom: [0.168, 0.106], top: [0.155, 0.1] });

  // The leg of a pair of shorts. It rides on the thigh, not the hips — hung
  // from the pelvis it would stay vertical while the leg swings out of it.
  const cuff = prism({ sides: 6, y0: -0.22, y1: 0.035, bottom: [0.074, 0.076], top: [0.086, 0.089] });

  const hipsSkirt = merge([
    prism({ sides: 8, y0: -0.36, y1: 0.08, bottom: [0.25, 0.21], top: [0.155, 0.1] }),
  ]);

  const upperArm = prism({ sides: 6, y0: -BODY.upperArm, y1: 0.02, bottom: [0.046, 0.046], top: [0.058, 0.058] });

  const forearm = merge([
    prism({ sides: 6, y0: -0.23, y1: 0.01, bottom: [0.033, 0.033], top: [0.041, 0.041] }),
    prism({ sides: 5, y0: -0.31, y1: -0.215, bottom: [0.028, 0.04], top: [0.034, 0.044] }),
  ]);

  // A long sleeve, riding on the forearm over all but the hand, a little
  // proud of it; at the wrist a cuff a shade darker.
  const sleeve = merge([
    prism({ sides: 6, y0: -0.2, y1: 0.015, bottom: [0.04, 0.04], top: [0.047, 0.047] }),
    prism({ sides: 6, y0: -0.225, y1: -0.2, bottom: [0.042, 0.042], top: [0.042, 0.042], shade: 0.8 }),
  ]);

  // A jacket's skirt, from the waist down over the hips, a little wider
  // than shorts and open below: worn on the hips' pivot.
  const hem = prism({ sides: 6, y0: -0.17, y1: 0.09, bottom: [0.182, 0.122], top: [0.166, 0.11] });

  // A knitted hat, worn on the head's pivot over the hair: a turned-up band,
  // the crown, and a bobble.
  const beanie = merge([
    prism({ sides: 10, y0: 0.165, y1: 0.215, bottom: [0.12, 0.128], top: [0.121, 0.129], offset: [0, 0.008], shade: 0.82 }),
    prism({ sides: 10, y0: 0.215, y1: 0.315, bottom: [0.118, 0.126], top: [0.106, 0.113], offset: [0, 0.008] }),
    prism({ sides: 10, y0: 0.315, y1: 0.35, bottom: [0.106, 0.113], top: [0.05, 0.054], offset: [0, 0.008] }),
    prism({ sides: 6, y0: 0.345, y1: 0.385, bottom: [0.03, 0.03], top: [0.022, 0.022], offset: [0, 0.008] }),
  ]);

  const thigh = prism({ sides: 6, y0: -BODY.thigh, y1: 0.03, bottom: [0.054, 0.056], top: [0.074, 0.078] });
  const shin = prism({ sides: 6, y0: -BODY.shin, y1: 0.01, bottom: [0.039, 0.041], top: [0.053, 0.056] });
  // Shoe: sole below the ankle, toe forward.
  const foot = merge([
    block(0.09, 0.07, 0.2, 0, -0.04, -0.055),
    block(0.08, 0.03, 0.08, 0, -0.005, 0.005),
  ]);

  // A motocross helmet, worn on the head's pivot in place of the hair: a
  // white shell, an orange chin bar and peak, goggles with a mirrored lens on
  // a navy strap — the player's own colours, so rider and bike read as one.
  // Painted in its own colours rather than tinted, so its instance is white.
  const shellWhite = 0xf4f1ea, orange = 0xf0772f, navy = 0x2e4670, lens = 0xe0913a;
  const helmet = merge([
    tinted(prism({ sides: 10, y0: 0.02, y1: 0.2, bottom: [0.122, 0.132], top: [0.132, 0.142], offset: [0, 0.012] }), shellWhite),
    tinted(prism({ sides: 10, y0: 0.2, y1: 0.3, bottom: [0.132, 0.142], top: [0.09, 0.1], offset: [0, 0.012] }), shellWhite),
    tinted(prism({ sides: 10, y0: 0.3, y1: 0.322, bottom: [0.09, 0.1], top: [0.045, 0.052], offset: [0, 0.012] }), orange),
    // A navy band round the crown, and the goggle strap round the back.
    tinted(prism({ sides: 10, y0: 0.235, y1: 0.262, bottom: [0.126, 0.136], top: [0.117, 0.127], offset: [0, 0.013] }), navy),
    tinted(prism({ sides: 10, y0: 0.14, y1: 0.185, bottom: [0.135, 0.145], top: [0.137, 0.147], offset: [0, 0.012] }), navy),
    // Chin bar, jutting forward and down, with its vented nose.
    tinted(block(0.17, 0.08, 0.1, 0, 0.055, -0.1), orange),
    tinted(prism({ sides: 4, y0: 0.02, y1: 0.11, bottom: [0.045, 0.03], top: [0.055, 0.035], offset: [0, -0.15] }), orange),
    // Goggles: frame and lens, filling the eye port.
    tinted(block(0.2, 0.068, 0.03, 0, 0.165, -0.132), navy),
    tinted(block(0.17, 0.046, 0.012, 0, 0.166, -0.15), lens),
    // The peak, tilted down over the goggles.
    tinted(block(0.19, 0.014, 0.16, 0, 0, 0).rotateX(0.28).translate(0, 0.285, -0.17), orange),
  ]);

  // A police officer's peaked cap, worn on the head's pivot over short hair:
  // a crown, a darker band round it, and a black peak over the eyes. The
  // crown takes the instance colour; band and peak are shades of it.
  const cap = merge([
    prism({ sides: 10, y0: 0.215, y1: 0.31, bottom: [0.112, 0.12], top: [0.128, 0.138], offset: [0, 0.008] }),
    prism({ sides: 10, y0: 0.31, y1: 0.33, bottom: [0.128, 0.138], top: [0.122, 0.132], offset: [0, 0.008] }),
    prism({ sides: 10, y0: 0.2, y1: 0.245, bottom: [0.113, 0.121], top: [0.114, 0.122], offset: [0, 0.008], shade: 0.35 }),
    block(0.2, 0.014, 0.075, 0, 0.215, -0.135, 0.06).rotateX(-0.15),
  ]);

  return { head, hairShort, hairLong, torso, hipsShorts, hipsSkirt, cuff, upperArm, forearm, sleeve, hem, beanie, thigh, shin, foot, helmet, cap };
}

/** Paint a finished part in one colour of its own (sRGB hex), instead of a grey shade. */
function tinted(geometry: BufferGeometry, hex: number): BufferGeometry {
  const c = new Color(hex);
  const colors = geometry.getAttribute('color');
  for (let i = 0; i < colors.count; i++) colors.setXYZ(i, c.r, c.g, c.b);
  return geometry;
}

export type PartGeometries = ReturnType<typeof createPartGeometries>;
